// ListingDetails.jsx
// Main component for displaying listing details - Modernized UI

import React from 'react';
import { COLORS, LAYOUT } from '../../constants';
import useListingDetails from './useListingDetails';
import LoadingState from './LoadingState';
import NotFoundState from './NotFoundState';
import ImageGallery from './ImageGallery';
import ListingInfo from './ListingInfo';
import ListingSpecs from './ListingSpecs';
import SellerInfo from './SellerInfo';
import ActionButtons from './ActionButtons';
import SoldBanner from './SoldBanner';
import BreadcrumbNav from './BreadcrumbNav';
import RecommendationsSection from './RecommendationsSection';

const ListingDetails = () => {
  const {
    listing,
    seller,
    loading,
    imgIndex,
    user,
    isOwner,
    showPrev,
    showNext,
    selectImage,
    handleMessageSeller
  } = useListingDetails();

  if (loading) {
    return <LoadingState />;
  }

  if (!listing) {
    return <NotFoundState />;
  }

  return (
    <div style={modernStyles.container}>
      {/* Breadcrumb Navigation */}
      <BreadcrumbNav listing={listing} />
      
      {/* Status Banner */}
      <SoldBanner listing={listing} />
      
      {/* Main Content */}
      <div style={modernStyles.contentWrapper}>
        <div style={modernStyles.contentLayout}>
          {/* Enhanced Image Gallery */}
          <div style={modernStyles.gallerySection}>
            <ImageGallery
              images={listing.images}
              imgIndex={imgIndex}
              onPrevious={showPrev}
              onNext={showNext}
              onSelectImage={selectImage}
            />
          </div>

          {/* Enhanced Info Panel */}
          <div style={modernStyles.infoPanel}>
            <ListingInfo listing={listing} />
            
            <ListingSpecs listing={listing} />
            
            <SellerInfo seller={seller} />
            
            {/* Enhanced Actions */}
            {user && (
              <ActionButtons
                listing={listing}
                seller={seller}
                onMessageSeller={handleMessageSeller}
                isOwner={isOwner}
              />
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Recommendations */}
      <RecommendationsSection currentListing={listing} />
    </div>
  );
}

// Modern styles using our design system
const modernStyles = {
  container: {
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
    paddingBottom: LAYOUT.spacing.xxxl,
  },
  contentWrapper: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: `${LAYOUT.spacing.xxxl} ${LAYOUT.spacing.lg}`,
  },
  contentLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 420px',
    gap: LAYOUT.spacing.xxxl,
    alignItems: 'start',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr',
      gap: LAYOUT.spacing.xxl,
    },
  },
  gallerySection: {
    position: 'sticky',
    top: LAYOUT.spacing.xl,
  },
  infoPanel: {
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xxxl,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${COLORS.border}`,
    height: 'fit-content',
  },
};

export default ListingDetails;